import React, { useEffect, useMemo, useState } from "react";
import { trackHome } from "@/lib/requestContext";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Star, ShieldCheck, CheckCircle2, Clock, Sparkles, ArrowRight, ChevronDown, MapPin, Wallet, Languages, Info, ArrowLeft, MessageSquare
} from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import TemplateBands from "@/components/marketplace/TemplateBands";

const CTA_LABEL_BY_TYPE = {
  instant_fixed_local: "Book now",
  hourly_local: "Book now",
  quote_local_project: "Request quote",
  digital_package: "Continue",
  custom_digital_project: "Request quote",
  consultation: "Schedule consult",
  recurring_service: "Start subscription",
};

function anonKey() {
  const k = "gcw_anon_key";
  let v = window.localStorage.getItem(k);
  if (!v) {
    v = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.localStorage.setItem(k, v);
  }
  return v;
}

export default function ServiceDetailPage() {
  useEffect(() => { trackHome("service_view"); }, []);
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  useDocumentTitle(data?.service?.title);
  const [notFound, setNotFound] = useState(false);
  const [pkgId, setPkgId] = useState(null);
  const [addons, setAddons] = useState({}); // id -> qty
  const [ctaLoading, setCtaLoading] = useState(false);
  const [messageBusy, setMessageBusy] = useState(false);
  const [categoryMap, setCategoryMap] = useState({});
  const [deal, setDeal] = useState(null);

  const contactProvider = async () => {
    if (!data?.provider) return;
    if (!user) {
      const next = encodeURIComponent(location.pathname + "?action=message");
      navigate(`/login?next=${next}`);
      return;
    }
    setMessageBusy(true);
    try {
      const { data: conv } = await api.post("/messages/direct", { to_provider_id: data.provider.id });
      navigate(`/dashboard/messages?conv=${conv.conversation_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not open the conversation");
    } finally {
      setMessageBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/services/${slug}`);
        setData(data);
        if (data.packages?.length) setPkgId(data.packages[0].id);
        // Track recently viewed
        try {
          const arr = JSON.parse(localStorage.getItem("gcw_recent_services") || "[]");
          const next = [data.service, ...arr.filter((s) => s.id !== data.service.id)].slice(0, 12);
          localStorage.setItem("gcw_recent_services", JSON.stringify(next));
        } catch (_) { /* ignore */ }
      } catch (err) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  // Resume-action after login: /services/:slug?action=message auto-opens
  // the contact-provider flow once auth + service data are ready.
  useEffect(() => {
    if (!data?.provider || !user) return;
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "message") {
      contactProvider();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, user]);

  useEffect(() => {
    (async () => {
      try {
        const cats = await api.get("/categories");
        setCategoryMap(Object.fromEntries(cats.data.map((c) => [c.id, c])));
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const total = useMemo(() => {
    if (!data) return 0;
    let t = 0;
    if (pkgId) {
      const p = data.packages.find((x) => x.id === pkgId);
      if (p) t += Number(p.price || 0);
    } else if (data.service.pricing_model !== "quote_required" && data.service.pricing_model !== "free_consultation") {
      t += Number(data.service.base_price || 0);
    }
    Object.entries(addons).forEach(([id, qty]) => {
      const a = data.addons.find((x) => x.id === id);
      if (a) t += Number(a.price || 0) * (qty || 1);
    });
    return Math.round(t * 100) / 100;
  }, [data, pkgId, addons]);
  useEffect(() => {
    if (data?.service?.id) api.get(`/deals/for-service/${data.service.id}`).then((r) => setDeal(r.data.deal)).catch(() => setDeal(null));
  }, [data?.service?.id]);
  const dealOff = useMemo(() => {
    if (!deal || !data) return 0;
    const base = pkgId ? Number(data.packages.find((x) => x.id === pkgId)?.price || 0) : Number(data.service.base_price || 0);
    const off = deal.discount_type === "percent" ? (base * deal.discount_value) / 100 : deal.discount_value;
    return Math.round(Math.min(off, base) * 100) / 100;
  }, [deal, data, pkgId]);

  const proceed = async () => {
    if (!data) return;
    // For quote/free-consult service types, we still route users to the
    // request-a-quote / message-provider flow. Everything else goes into the
    // Phase-3 checkout.
    if (!user) {
      const params = new URLSearchParams({ next: `/checkout?service=${encodeURIComponent(data.service.id)}` });
      navigate(`/login?${params.toString()}`);
      return;
    }
    if (data.service.service_type === "quote_local_project" || data.service.service_type === "custom_digital_project") {
      // Include the service's provider as an invited recipient of the request.
      const invite = data.provider?.id ? `&invite=${encodeURIComponent(data.provider.id)}` : "";
      navigate(`/request-a-service?fromService=${encodeURIComponent(data.service.id)}${invite}`);
      return;
    }
    setCtaLoading(true);
    try {
      const addon_ids = Object.entries(addons).filter(([, q]) => q > 0).map(([id]) => id);
      const search = new URLSearchParams({ service: data.service.id });
      if (pkgId) search.set("package", pkgId);
      if (addon_ids.length) search.set("addons", addon_ids.join(","));
      if (deal) search.set("deal", deal.id);
      navigate(`/checkout?${search.toString()}`, {
        state: { service: data.service, pkgId, addonIds: addon_ids },
      });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not proceed");
    } finally {
      setCtaLoading(false);
    }
  };

  if (notFound) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-[#071b22]">Service not found.</h1>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-[#165a71] font-medium"><ArrowLeft className="h-4 w-4" /> Back to services</Link>
        </div>
      </PublicLayout>
    );
  }

  if (!data) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-[#252A34]/60">Loading…</div>
      </PublicLayout>
    );
  }

  const s = data.service;
  const provider = data.provider;
  const cta = CTA_LABEL_BY_TYPE[s.service_type] || "Continue";

  return (
    <PublicLayout>
      <TemplateBands entity="services" slot="header" />
      {/* Hero band */}
      <div className="relative bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-6 pb-10 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 text-xs text-[#252A34]/60 mb-3">
              <Link to="/services" className="hover:text-[#165a71]">Services</Link>
              <span>/</span>
              <span className="capitalize">{s.kind}</span>
              {s.category_id && (
                <>
                  <span>/</span>
                  <span>{categoryMap[s.category_id]?.name || ""}</span>
                </>
              )}
            </div>
            <h1 translate="no" className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="service-title">
              {s.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 text-amber-600 font-medium">
                <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                {(s.rating_avg || 0).toFixed(1)} <span className="text-[#252A34]/60">({s.rating_count} reviews)</span>
              </span>
              <span className="text-[#252A34]/70 inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {s.instant_accept ? "Instant book" : "Manual confirm"}</span>
              <span className="text-[#252A34]/70 inline-flex items-center gap-1"><Languages className="h-4 w-4" /> {(s.languages || []).join(", ")}</span>
              {s.is_featured && <Badge className="rounded-full bg-amber-100 text-amber-800 border border-amber-200"><Sparkles className="h-3 w-3 mr-1" /> Featured</Badge>}
            </div>

            {/* Gallery */}
            <div className="mt-6 grid grid-cols-6 gap-2 rounded-2xl overflow-hidden aspect-[16/8]" data-testid="service-gallery">
              <img src={s.featured_image} alt={s.title} className="col-span-6 md:col-span-4 h-full w-full object-cover" />
              <div className="hidden md:grid col-span-2 grid-rows-2 gap-2">
                {(s.gallery?.slice(0, 2) || [{ url: s.featured_image }, { url: s.featured_image }]).map((g, i) => (
                  <img key={i} src={g.url || s.featured_image} alt="" className="h-full w-full object-cover" />
                ))}
              </div>
            </div>

            {/* About */}
            <section className="mt-10" data-testid="service-about">
              <SectionTitle>About this service</SectionTitle>
              <p translate="no" className="whitespace-pre-line text-[#252A34]/85 leading-relaxed">{s.full_description || s.short_description}</p>
              {s.search_tags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.search_tags.slice(0, 8).map((t) => (
                    <Badge key={t} variant="outline" className="rounded-full border-slate-200 text-[#252A34]/70">{t}</Badge>
                  ))}
                </div>
              )}
            </section>

            {/* Packages */}
            {data.packages?.length > 0 && (
              <section className="mt-10" data-testid="service-packages">
                <SectionTitle>Compare packages</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {data.packages.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPkgId(p.id)}
                      data-testid={`package-${p.tier}`}
                      className={`text-left rounded-2xl border p-5 flex flex-col gap-3 hover:border-[#165a71]/40 transition-colors ${
                        pkgId === p.id ? "border-[#165a71] ring-2 ring-[#165a71]/20 bg-[#165a71]/5" : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-baseline justify-between">
                        <div className="font-display text-base font-semibold text-[#071b22] uppercase tracking-wider">{p.name}</div>
                        <div className="font-display text-2xl text-[#071b22] font-semibold">${p.price}</div>
                      </div>
                      {p.short_description && <div translate="no" className="text-sm text-[#252A34]/70">{p.short_description}</div>}
                      {p.delivery_time_days && (
                        <div className="text-xs text-[#252A34]/60 inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" /> {p.delivery_time_days}-day delivery
                        </div>
                      )}
                      <ul className="space-y-1.5 mt-2">
                        {(p.features || []).map((f, i) => (
                          <li key={i} className="text-sm text-[#252A34] flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> {f}
                          </li>
                        ))}
                      </ul>
                      {p.revisions !== undefined && p.revisions !== null && (
                        <div className="mt-auto text-xs text-[#252A34]/60">Includes {p.revisions === 0 ? "no" : p.revisions} revisions</div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Add-ons */}
            {data.addons?.length > 0 && (
              <section className="mt-10" data-testid="service-addons">
                <SectionTitle>Add-ons</SectionTitle>
                <div className="rounded-2xl border border-slate-200 bg-white divide-y">
                  {data.addons.map((a) => (
                    <label key={a.id} className="flex items-center justify-between gap-4 p-4">
                      <div>
                        <div className="font-medium text-[#071b22]">{a.name}</div>
                        {a.description && <div className="text-xs text-[#252A34]/60">{a.description}</div>}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="font-display font-semibold text-[#071b22]">+${a.price}</div>
                        <input
                          type="checkbox"
                          checked={(addons[a.id] || 0) > 0}
                          onChange={(e) => setAddons({ ...addons, [a.id]: e.target.checked ? 1 : 0 })}
                          data-testid={`addon-${a.id}`}
                          className="h-5 w-5 rounded border-slate-300 text-[#165a71]"
                        />
                      </div>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Requirements */}
            {data.requirements?.length > 0 && (
              <section className="mt-10" data-testid="service-requirements">
                <SectionTitle>What we'll need from you</SectionTitle>
                <ul className="rounded-2xl border border-slate-200 bg-white divide-y">
                  {data.requirements.map((r) => (
                    <li key={r.id} className="p-4">
                      <div className="font-medium text-[#071b22]">{r.question} {r.required && <span className="text-red-600 text-xs ml-1">required</span>}</div>
                      {r.instructions && <div className="text-xs text-[#252A34]/60 mt-1">{r.instructions}</div>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* FAQs */}
            {data.faqs?.length > 0 && (
              <section className="mt-10" data-testid="service-faqs">
                <SectionTitle>FAQs</SectionTitle>
                <Accordion type="single" collapsible className="rounded-2xl border border-slate-200 bg-white divide-y">
                  {data.faqs.map((f) => (
                    <AccordionItem key={f.id} value={f.id} className="border-none">
                      <AccordionTrigger className="px-4 py-4 hover:no-underline">{f.question}</AccordionTrigger>
                      <AccordionContent className="px-4 text-[#252A34]/80">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            {/* Policies */}
            <section className="mt-10" data-testid="service-policies">
              <SectionTitle>Policies</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {[
                  ["Cancellation", s.cancellation_policy],
                  ["Rescheduling", s.rescheduling_policy],
                  ["Refunds", s.refund_policy],
                  ["Revisions", s.revision_policy],
                  ["Late arrival", s.late_arrival_policy],
                  ["No-shows", s.no_show_policy],
                  ["Weather", s.weather_policy],
                ].filter(([, v]) => v).map(([label, v]) => (
                  <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs uppercase tracking-wider text-[#252A34]/60 font-medium">{label}</div>
                    <div className="mt-1 text-[#252A34]">{v}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Reviews placeholder */}
            <section className="mt-10">
              <SectionTitle>Reviews</SectionTitle>
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="reviews-placeholder">
                <Star className="h-6 w-6 text-amber-500 mx-auto" />
                <div className="mt-2 font-display text-[#071b22] font-semibold">Reviews launch in Phase 5.</div>
                <div className="text-sm text-[#252A34]/70 mt-1">Ratings shown above are aggregated from past jobs.</div>
              </div>
            </section>
          </div>

          {/* Sticky CTA card */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="cta-card">
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-3xl font-semibold text-[#071b22]" data-testid="cta-total">
                    ${(Math.round(((total || 0) - dealOff) * 100) / 100).toFixed(dealOff % 1 || total % 1 ? 2 : 0)}
                    {dealOff > 0 && <span className="ml-2 text-base font-normal text-[#252A34]/45 line-through" data-testid="cta-original-total">${total}</span>}
                  </div>
                  <div className="text-xs text-[#252A34]/60 uppercase tracking-wider">{s.pricing_model?.replace(/_/g, " ")}</div>
                </div>
                {deal && (
                  <div className="mt-3 rounded-xl bg-[#d97757]/10 border border-[#d97757]/30 px-3 py-2 text-xs text-[#8a3b1e]" data-testid="service-deal-banner">
                    <span className="font-bold">Deal: {deal.title}</span> · {deal.remaining} left · ends {new Date(deal.ends_at).toLocaleDateString()}
                    {deal.description && <div className="mt-0.5 text-[#8a3b1e]/80">{deal.description}</div>}
                  </div>
                )}
                {pkgId && data.packages?.length > 0 && (
                  <div className="mt-2 text-xs text-[#252A34]/70">
                    Selected: {data.packages.find((p) => p.id === pkgId)?.name}
                  </div>
                )}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={proceed}
                  disabled={ctaLoading}
                  data-testid="cta-proceed"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-3 font-medium hover:bg-[#11485a] transition-colors disabled:opacity-60"
                >
                  {ctaLoading ? "Saving…" : (<>{cta} <ArrowRight className="h-4 w-4" /></>)}
                </motion.button>
                <button
                  onClick={contactProvider}
                  disabled={messageBusy}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-medium text-[#071b22] hover:bg-slate-50 disabled:opacity-60"
                  data-testid="cta-message"
                >
                  <MessageSquare className="h-4 w-4" /> {messageBusy ? "Opening…" : "Contact provider"}
                </button>
                <div className="mt-4 text-[11px] text-[#252A34]/60 flex items-start gap-2">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>Phase 3 uses a DEV "Simulate test payment" button after checkout — Stripe launches in Phase 4.</span>
                </div>
              </div>

              {/* Provider card */}
              {provider && (
                <Link to={`/providers/${provider.slug}`} className="block rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#165a71]/40 transition-colors" data-testid="provider-card-sidebar">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 rounded-2xl">
                      <AvatarImage src={provider.profile_image} alt={provider.display_name} />
                      <AvatarFallback className="rounded-2xl bg-[#165a71] text-white">
                        {(provider.business_name || provider.display_name || "GC").split(" ").map((p) => p[0]).slice(0, 2).join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div translate="no" className="font-display font-semibold text-[#071b22] truncate">{provider.business_name || provider.display_name}</div>
                      <div translate="no" className="text-xs text-[#252A34]/60 truncate">{provider.tagline}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3 text-xs text-[#252A34]/70">
                    <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" /> {(provider.rating_avg || 0).toFixed(1)}</span>
                    <span>·</span>
                    <span>{provider.completed_jobs || 0} jobs</span>
                    {provider.verifications?.gcw_verified?.status === "approved" && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Verified</span>
                      </>
                    )}
                  </div>
                </Link>
              )}

              {/* Details */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm" data-testid="service-details">
                <div className="font-display font-semibold text-[#071b22] mb-3">Service details</div>
                <div className="space-y-2 text-[#252A34]/80">
                  {s.delivery_deadline_days && <Row label="Delivery" value={`${s.delivery_deadline_days} days`} />}
                  {s.revision_limit !== null && s.revision_limit !== undefined && <Row label="Revisions" value={s.revision_limit === 0 ? "None" : s.revision_limit} />}
                  {s.same_day_availability && <Row label="Same-day" value="Available" />}
                  {s.emergency_availability && <Row label="Emergency" value="Available" />}
                  {s.service_radius_miles && <Row label="Service radius" value={`${s.service_radius_miles} mi`} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TemplateBands entity="services" slot="footer" />
    </PublicLayout>
  );
}

const SectionTitle = ({ children }) => (
  <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#071b22] mb-4">{children}</h2>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#252A34]/60">{label}</span>
    <span className="font-medium text-[#071b22]">{value}</span>
  </div>
);
